import React, { useEffect, useState, useRef } from 'react';

function DaisyHead({ size = 44, delay = 0, palette = 'white', mobileOnly = false }) {
  const petalCount = 10;
  const petalColor = palette === 'pink' ? '#f3dbe3' : palette === 'purple' ? '#e6d6f2' : '#ffffff';
  const petalStroke = palette === 'pink' ? '#e4bfcc' : palette === 'purple' ? '#c9b3e0' : '#efe4c9';
  return (
    <div
      className={`daisy ${mobileOnly ? 'daisy-mobile-only' : ''}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="daisy-sway" style={{ animationDelay: `${delay + 0.6}s` }}>
        <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <g>
            {Array.from({ length: petalCount }).map((_, i) => {
              const angle = (i * 360) / petalCount;
              return (
                <ellipse
                  key={i}
                  cx="50"
                  cy="22"
                  rx="9"
                  ry="17"
                  fill={petalColor}
                  stroke={petalStroke}
                  strokeWidth="1.2"
                  transform={`rotate(${angle} 50 50)`}
                />
              );
            })}
            <circle cx="50" cy="50" r="9" fill="#f1c77a" stroke="#c99050" strokeWidth="1.2" />
            <circle cx="47" cy="48" r="2" fill="#c99050" opacity="0.5" />
          </g>
        </svg>
      </div>
    </div>
  );
}

// Deterministic pseudo-random for stable per-page variety
function pickPalette(pageKey, idx, side) {
  const cycles = {
    sobre:    ['white', 'pink',   'white',  'purple', 'white', 'pink'],
    libro:    ['pink',  'white',  'pink',   'white',  'purple', 'white'],
    tarot:    ['purple','white',  'purple', 'pink',   'white',  'purple'],
    contacto: ['white', 'purple', 'white',  'pink',   'purple', 'white']
  };
  const cycle = cycles[pageKey] || cycles.sobre;
  const offset = side === 'right' ? 3 : 0;
  return cycle[(idx + offset) % cycle.length];
}

/**
 * DaisyEdges renders two columns of daisies on the sides of the page.
 * The number of daisies adapts to the full scrollable page height so they
 * keep a constant density from top to bottom, even on long pages.
 */
export default function DaisyEdges({ pageKey = 'sobre' }) {
  const [daisyCount, setDaisyCount] = useState(10);

  useEffect(() => {
    const computeCount = () => {
      // Tarot keeps the dynamic, higher count (scales with content height).
      // All other pages: fixed 6 per side, evenly distributed.
      if (pageKey !== 'tarot') {
        setDaisyCount(6);
        return;
      }
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const isMobile = vw < 768;
      const density = isMobile ? 130 : 150;

      const docEl = document.documentElement;
      const body = document.body;
      const pageHeight = Math.max(
        body.scrollHeight || 0,
        docEl.scrollHeight || 0,
        body.offsetHeight || 0,
        docEl.offsetHeight || 0,
        window.innerHeight || 0
      );

      const count = Math.max(6, Math.ceil(pageHeight / density) - 2);
      setDaisyCount(count);
    };

    computeCount();

    const ro = new ResizeObserver(() => computeCount());
    ro.observe(document.body);
    window.addEventListener('resize', computeCount);

    const t1 = setTimeout(computeCount, 200);
    const t2 = setTimeout(computeCount, 800);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', computeCount);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pageKey]);

  // Desktop sizes: 10% bigger than before (was 52-70 → now 58-77)
  const desktopSizeFor = (i, side) => {
    if (side === 'right') return i % 2 === 0 ? 64 : 77;
    return i % 2 === 0 ? 71 : 58;
  };

  const items = Array.from({ length: daisyCount });

  return (
    <>
      <div className="daisy-col left" aria-hidden="true">
        {items.map((_, i) => (
          <DaisyHead
            key={`l-${pageKey}-${i}`}
            size={desktopSizeFor(i, 'left')}
            palette={pickPalette(pageKey, i, 'left')}
            delay={0.09 * i}
            mobileOnly={false}
          />
        ))}
      </div>
      <div className="daisy-col right" aria-hidden="true">
        {items.map((_, i) => (
          <DaisyHead
            key={`r-${pageKey}-${i}`}
            size={desktopSizeFor(i, 'right')}
            palette={pickPalette(pageKey, i, 'right')}
            delay={0.09 * i + 0.06}
            mobileOnly={false}
          />
        ))}
      </div>
    </>
  );
}
